import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { isValidAssociatesTag } from "@/lib/amazonAffiliate";
import type { Workspace } from "@/hooks/useWorkspace";

type Props = { workspace: Workspace };

const MARKETPLACE_OPTIONS = [
  { host: "webservices.amazon.com", label: "United States (.com)" },
  { host: "webservices.amazon.co.uk", label: "United Kingdom (.co.uk)" },
  { host: "webservices.amazon.ca", label: "Canada (.ca)" },
  { host: "webservices.amazon.de", label: "Germany (.de)" },
  { host: "webservices.amazon.fr", label: "France (.fr)" },
  { host: "webservices.amazon.com.au", label: "Australia (.com.au)" },
] as const;

type CredMeta = {
  partner_tag: string;
  marketplace_host: string;
  status: string;
  error_message: string | null;
  last_verified_at: string | null;
};

const ACCESS_KEY_RE = /^[A-Z0-9]{16,32}$/;

/**
 * Workspace-scoped Amazon Product Advertising API (PA-API) credential card.
 *
 * Per-workspace BYOK only — Excerptus never holds the credentials. The keys
 * are encrypted server-side; this card only displays metadata after save.
 *
 * No live enrichment is wired up yet. This card just stores credentials and
 * verifies them with a single GetItems call so future enrichment features
 * can use them.
 */
export const AmazonPaapiSettingsCard = ({ workspace }: Props) => {
  const [meta, setMeta] = useState<CredMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const [accessKeyId, setAccessKeyId] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [partnerTag, setPartnerTag] = useState("");
  const [marketplaceHost, setMarketplaceHost] =
    useState<(typeof MARKETPLACE_OPTIONS)[number]["host"]>("webservices.amazon.com");

  const refresh = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("workspace_amazon_paapi_credentials")
      .select("partner_tag, marketplace_host, status, error_message, last_verified_at")
      .eq("workspace_id", workspace.id)
      .maybeSingle();
    if (error && error.code !== "PGRST116") toast.error(error.message);
    setMeta((data as CredMeta | null) ?? null);
    if (data) {
      setPartnerTag(data.partner_tag);
      setMarketplaceHost(
        data.marketplace_host as (typeof MARKETPLACE_OPTIONS)[number]["host"],
      );
    } else {
      // Default partner tag from the existing affiliate card
      const { data: prof } = await supabase
        .from("profiles")
        .select("amazon_associates_tag")
        .eq("id", workspace.id)
        .maybeSingle();
      if (prof?.amazon_associates_tag) setPartnerTag(prof.amazon_associates_tag);
    }
    setLoading(false);
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace.id]);

  const trimmedAk = accessKeyId.trim();
  const trimmedSk = secretKey.trim();
  const trimmedTag = partnerTag.trim();
  const akInvalid = trimmedAk.length > 0 && !ACCESS_KEY_RE.test(trimmedAk);
  const skInvalid = trimmedSk.length > 0 && (trimmedSk.length < 30 || /\s/.test(trimmedSk));
  const tagInvalid = trimmedTag.length > 0 && !isValidAssociatesTag(trimmedTag);

  const canSave =
    !loading &&
    !saving &&
    !akInvalid &&
    !skInvalid &&
    !tagInvalid &&
    trimmedAk.length > 0 &&
    trimmedSk.length > 0 &&
    trimmedTag.length > 0;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    const { data, error } = await supabase.functions.invoke("amazon-paapi-connect", {
      body: {
        workspaceId: workspace.id,
        accessKeyId: trimmedAk,
        secretKey: trimmedSk,
        partnerTag: trimmedTag,
        marketplaceHost,
      },
    });
    setSaving(false);
    const errMsg =
      (data as { error?: string } | null)?.error ?? error?.message ?? null;
    if (errMsg) {
      toast.error(errMsg);
      return;
    }
    toast.success("Amazon PA-API credentials verified and saved");
    setAccessKeyId("");
    setSecretKey("");
    void refresh();
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    const { data, error } = await supabase.functions.invoke("amazon-paapi-disconnect", {
      body: { workspaceId: workspace.id },
    });
    setDisconnecting(false);
    const errMsg =
      (data as { error?: string } | null)?.error ?? error?.message ?? null;
    if (errMsg) {
      toast.error(errMsg);
      return;
    }
    toast.success("Disconnected Amazon PA-API");
    void refresh();
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle>Amazon Product Advertising API</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5 text-sm">
        <div className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
          <strong className="text-foreground">Most users won't need this.</strong>{" "}
          If you only link to books with an ISBN, the regular Amazon Associates
          tag in the card above is usually enough — Excerptus builds a valid
          affiliate link directly from the ISBN. Connect PA-API only if you
          want verified ASIN lookups, marketplace-specific links, or
          cover/price/availability data for books where ISBN→ASIN doesn't
          resolve cleanly.
        </div>
        <p className="text-muted-foreground">
          Connect this workspace's Amazon Product Advertising API credentials so
          future features can enrich library items with live cover, price, and
          availability data. Excerptus never holds these credentials — they're
          encrypted and scoped to this workspace.
        </p>
        <p className="text-xs text-muted-foreground">
          Note: Amazon requires an active Associates account with at least
          three qualifying sales in the prior 180 days to keep PA-API access
          enabled.{" "}
          <a
            href="https://webservices.amazon.com/paapi5/documentation/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            PA-API docs
          </a>
        </p>

        {loading ? (
          <Skeleton className="h-12 w-full" />
        ) : meta ? (
          <div className="rounded-md border bg-muted/30 p-3 space-y-1 text-xs">
            <div className="flex items-center gap-2">
              <Badge variant={meta.status === "connected" ? "default" : "destructive"}>
                {meta.status === "connected" ? "Connected" : "Error"}
              </Badge>
              <span className="text-muted-foreground">
                Partner Tag: <code>{meta.partner_tag}</code>
              </span>
            </div>
            <div className="text-muted-foreground">
              Marketplace: <code>{meta.marketplace_host}</code>
            </div>
            {meta.last_verified_at && (
              <div className="text-muted-foreground">
                Last verified: {new Date(meta.last_verified_at).toLocaleString()}
              </div>
            )}
            {meta.error_message && (
              <div className="text-destructive">{meta.error_message}</div>
            )}
          </div>
        ) : (
          <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
            Not connected
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={`paapi-ak-${workspace.id}`}>Access Key ID</Label>
            <Input
              id={`paapi-ak-${workspace.id}`}
              value={accessKeyId}
              onChange={(e) => setAccessKeyId(e.target.value)}
              placeholder={meta ? "•••••• (stored)" : "AKIA…"}
              autoComplete="off"
              spellCheck={false}
              aria-invalid={akInvalid}
            />
            {akInvalid && (
              <p className="text-xs text-destructive">16–32 chars, A–Z and 0–9 only.</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`paapi-sk-${workspace.id}`}>Secret Key</Label>
            <Input
              id={`paapi-sk-${workspace.id}`}
              type="password"
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
              placeholder={meta ? "•••••• (stored)" : "Secret access key"}
              autoComplete="new-password"
              spellCheck={false}
              aria-invalid={skInvalid}
            />
            {skInvalid && (
              <p className="text-xs text-destructive">30–80 chars, no whitespace.</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`paapi-tag-${workspace.id}`}>Partner Tag</Label>
            <Input
              id={`paapi-tag-${workspace.id}`}
              value={partnerTag}
              onChange={(e) => setPartnerTag(e.target.value)}
              placeholder="yourbrand-20"
              aria-invalid={tagInvalid}
            />
            {tagInvalid && (
              <p className="text-xs text-destructive">
                Use the same format as your Associates tag (e.g. <code>yourbrand-20</code>).
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`paapi-mkt-${workspace.id}`}>Marketplace</Label>
            <Select
              value={marketplaceHost}
              onValueChange={(v) =>
                setMarketplaceHost(v as (typeof MARKETPLACE_OPTIONS)[number]["host"])
              }
            >
              <SelectTrigger id={`paapi-mkt-${workspace.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MARKETPLACE_OPTIONS.map((m) => (
                  <SelectItem key={m.host} value={m.host}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          Saving runs one verification call against PA-API. Credentials are
          only stored if Amazon accepts them.
        </p>

        <div className="flex flex-wrap gap-2 justify-end">
          {meta && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" disabled={disconnecting}>
                  {disconnecting ? "Disconnecting…" : "Disconnect"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Disconnect Amazon PA-API?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This removes the stored credentials for this workspace.
                    Affiliate URL building still works — only future PA-API
                    enrichment is disabled.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDisconnect}>
                    Disconnect
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <Button onClick={handleSave} disabled={!canSave}>
            {saving ? "Verifying…" : meta ? "Replace credentials" : "Verify & save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
